import { useCallback, useRef, useState } from "react";
import { LuLoader2, LuMoreHorizontal, LuRotateCcw, LuXCircle } from "react-icons/lu";
import useDismiss, { DismissReason } from "../../../hooks/useDismiss";
import { handleMenuKeys } from "../../../app-components/header/menuKeys";

type Props = {
  status: string;
  busy: boolean;
  openUp?: boolean;
  onClose: () => void;
  onReopen: () => void;
};

const item = "flex w-full items-center gap-2.5 px-3.5 py-2 text-left text-sm transition-colors focus:outline-none";

/** Click-to-open row menu: Close for pending requests, Reopen for closed ones */
const RequestRowActions = ({ status, busy, openUp, onClose, onReopen }: Props) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const s = status.toLowerCase();
  const canClose = s === "pending";
  const canReopen = s === "closed";

  const dismiss = useCallback((reason: DismissReason) => {
    setOpen(false);
    if (reason === "escape") btnRef.current?.focus();
  }, []);
  useDismiss(wrapRef, open, dismiss);

  if (!canClose && !canReopen) {
    return <span className="text-hcMuted" title="No admin actions for accepted or rejected requests">–</span>;
  }

  const run = (fn: () => void) => {
    setOpen(false);
    fn();
  };

  return (
    <div ref={wrapRef} className="relative inline-block">
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={busy}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={busy ? "Saving" : "Actions"}
        className="flex h-8 w-8 items-center justify-center rounded-lg text-honeConnectGray transition-colors hover:bg-hcBg hover:text-honeConnectBlack disabled:cursor-wait focus:outline-none focus-visible:ring-2 focus-visible:ring-honeConnectPurple/40"
      >
        {busy ? <LuLoader2 className="h-4 w-4 animate-spin" aria-hidden /> : <LuMoreHorizontal className="h-5 w-5" aria-hidden />}
      </button>

      {open && (
        <div
          role="menu"
          onKeyDown={handleMenuKeys}
          className={`absolute right-0 z-30 w-44 overflow-hidden rounded-xl border border-hcBorder bg-white py-1.5 text-left shadow-pop ${
            openUp ? "bottom-full mb-1" : "top-full mt-1"
          }`}
        >
          {canClose && (
            <button type="button" role="menuitem" onClick={() => run(onClose)} className={`${item} text-red-600 hover:bg-red-50 focus:bg-red-50`}>
              <LuXCircle className="h-4 w-4" aria-hidden />
              Close request
            </button>
          )}
          {canReopen && (
            <button type="button" role="menuitem" onClick={() => run(onReopen)} className={`${item} text-honeConnectBlack hover:bg-hcBg focus:bg-hcBg`}>
              <LuRotateCcw className="h-4 w-4 text-hcMuted" aria-hidden />
              Reopen request
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default RequestRowActions;
